import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  { label: 'HRMS Platform', to: '/hrms' },
  { label: 'Web Development', to: '/web-dev' },
  { label: 'Manpower Recruitment', to: '/recruitment' },
];

const company = [
  { label: 'About Us', to: '/about' },
  { label: 'Contact', to: '/contact' },
  { label: 'Careers', to: '/contact' },
];

const securityItems = [
  { icon: '🔐', label: 'Role-Based Access Control' },
  { icon: '🔒', label: 'Data Encrypted in Transit & at Rest' },
  { icon: '🛡️', label: 'Secure Cloud Infrastructure' },
  { icon: '👁️', label: 'Regular Security Reviews' },
];

export default function Footer() {
  return (
    <footer className="bg-bg-primary border-t border-white/5 pt-16 pb-8 relative overflow-hidden">
      {/* Top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-px bg-gradient-to-r from-transparent via-accent-teal/30 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-accent-teal/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">

          {/* Brand */}
          <div className="lg:col-span-1 flex flex-col gap-4 sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2">
              <img src="/trivologo.png" alt="Trivo Logo" className="h-8 w-auto" />
              <span className="font-body text-sm text-text-muted font-medium mt-1">
                Talent Trend Solutions
              </span>
            </Link>
            <p className="text-text-muted text-sm leading-7 max-w-xs">
              Empowering businesses through intelligent HR technology, bespoke digital
              products, and strategic talent solutions.
            </p>
            <span className="font-mono text-[10px] text-accent-teal/60 tracking-[0.2em] uppercase">
              Talent. Technology. Transformation.
            </span>
            <div className="flex items-center gap-3 mt-1">
              {['in', 'tw', 'ig'].map((s) => (
                <motion.a
                  key={s}
                  href="#"
                  className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-text-muted hover:text-accent-teal hover:border-accent-teal/30 transition-colors text-xs font-mono"
                  whileHover={{ y: -2, scale: 1.05 }}
                  aria-label={s}
                >
                  {s}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="flex flex-col gap-4">
            <h5 className="font-display font-bold text-xs uppercase tracking-widest text-text-primary">
              Services
            </h5>
            <ul className="flex flex-col gap-3">
              {services.map((s) => (
                <li key={s.to}>
                  <Link to={s.to} className="text-sm text-text-muted hover:text-accent-teal transition-colors duration-200">
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="flex flex-col gap-4">
            <h5 className="font-display font-bold text-xs uppercase tracking-widest text-text-primary">
              Company
            </h5>
            <ul className="flex flex-col gap-3">
              {company.map((c) => (
                <li key={c.label}>
                  <Link to={c.to} className="text-sm text-text-muted hover:text-accent-teal transition-colors duration-200">
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Security & Trust */}
          <div className="flex flex-col gap-4">
            <h5 className="font-semibold text-xs uppercase tracking-widest text-accent-teal">
              Security & Trust
            </h5>
            <ul className="flex flex-col gap-2.5">
              {securityItems.map((item) => (
                <li key={item.label} className="flex items-start gap-2 text-text-muted text-xs leading-snug">
                  <span className="flex-shrink-0 text-sm leading-none mt-0.5">{item.icon}</span>
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Demo CTA Card */}
          <div className="glass glass-teal p-5 flex flex-col gap-3">
            <span className="font-mono text-[10px] text-accent-teal tracking-widest uppercase">
              Get Started
            </span>
            <h5 className="font-display font-bold text-base text-text-primary leading-tight">
              Ready to transform your business?
            </h5>
            <p className="text-text-muted text-xs leading-relaxed">
              HRMS, Web Development, or Recruitment — let's talk.
            </p>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-full bg-accent-teal text-bg-primary font-semibold text-sm btn-glow-teal mt-1"
              >
                Request a Demo
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/8 to-transparent mb-8" />

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 flex-wrap">
          <p className="text-text-muted text-xs opacity-50">
            &copy; {new Date().getFullYear()} Trivo Talent Trend Solutions. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-text-muted text-xs hover:text-text-primary transition-colors opacity-60 hover:opacity-100">
              Privacy Policy
            </Link>
            <span className="text-text-muted/30 text-xs">·</span>
            <Link to="/terms" className="text-text-muted text-xs hover:text-text-primary transition-colors opacity-60 hover:opacity-100">
              Terms of Service
            </Link>
          </div>
          <p className="text-text-muted text-xs opacity-50">
            Built with precision. Powered by purpose.
          </p>
        </div>
      </div>
    </footer>
  );
}
